import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowRedirect } from '@eneco/flows/hooks';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioGroup, RadioTile } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { GenericRadioChoiceStepRendering } from '@sitecore/types/GenericRadioChoiceStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';

interface FormFields {
  employeeCount: string;
}

export const EmployeeCountStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const flowContext = useFlowSelector(state => state.context);
  const { customRedirect } = useFlowRedirect();

  const { GenericRadioChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    GenericRadioChoiceStep: GenericRadioChoiceStepRendering;
  }>();

  const formSchema = yup.object({
    employeeCount: yup.string().required(textualData?.choice?.genericContentRadio?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      employeeCount: flowContext.employeeCount,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const employeeCountValue = formData.employeeCount;

      const selectedOption = textualData?.choice?.genericContentRadio?.value?.options?.find(
        (option: { name?: string }) => option.name === employeeCountValue,
      ) as { name: string; label: string; illustration: string; redirectLink?: { href: string } } | undefined;
      const redirectLink = selectedOption?.redirectLink;

      if (redirectLink?.href) {
        const normalizedPostalCode = flowContext.postalCode?.replace(' ', '');
        const houseNumberString = flowContext.houseNumber?.toString();

        customRedirect(redirectLink.href, {
          queryParams: {
            postalCode: normalizedPostalCode,
            houseNumber: houseNumberString,
            houseNumberSuffix: flowContext.houseNumberSuffix,
          },
          cleanBasket: false,
        });
        return;
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          employeeCount: employeeCountValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.content?.value && <RichText html={textualData.content.content.value} />}
      </Header>

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <Controller
        control={control}
        name="employeeCount"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby="employeeCount"
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.employeeCount?.message}>
            {textualData?.choice?.genericContentRadio?.value?.options?.map(
              ({ name, label }: { name: string; label: string }) => (
                <RadioTile key={name} value={name}>
                  {label}
                </RadioTile>
              ),
            )}
          </RadioGroup>
        )}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
